import { useEffect, useState } from "react";

/** Whole seconds left until `deadline`, or 0 when there is none / it passed. */
const secondsLeft = (deadline: number | null) =>
  deadline ? Math.max(0, Math.ceil((deadline - Date.now()) / 1000)) : 0;

/**
 * Counts down to `deadline` (epoch ms) and returns the seconds remaining.
 * The value is derived during render - the interval only forces the re-render -
 * so it is correct on the very first render after a deadline is set, and it
 * stays honest when the browser throttles background tabs.
 */
const useCountdown = (deadline: number | null) => {
  const [, tick] = useState(0);

  useEffect(() => {
    if (!deadline || deadline <= Date.now()) return;

    const id = setInterval(() => {
      tick((n) => n + 1);
      if (Date.now() >= deadline) clearInterval(id);
    }, 1000);

    return () => clearInterval(id);
  }, [deadline]);

  return secondsLeft(deadline);
};

/**
 * Formats seconds as a m:ss timer while minutes remain ("29:59 min"), then
 * switches to a plain seconds count for the final minute ("45 sec"). Shared by
 * every auth countdown so they all read the same.
 */
export const formatCountdown = (totalSeconds: number) => {
  const safe = Math.max(0, totalSeconds);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return minutes > 0
    ? `${minutes}:${seconds.toString().padStart(2, "0")} min`
    : `${seconds} sec`;
};

export default useCountdown;
